import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { Database, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface LiveModePlaceholderProps {
  readonly title?: string;
  readonly description?: string;
  readonly onRetry?: () => void;
  readonly isLoading?: boolean;
  readonly variant?: 'skeleton' | 'empty' | 'error';
  readonly className?: string;
}

export function LiveModePlaceholder({
  title = 'Loading data...',
  description = 'Fetching data from the API',
  onRetry,
  isLoading = true,
  variant = 'skeleton',
  className = '',
}: LiveModePlaceholderProps): React.ReactElement {
  if (variant === 'skeleton') {
    return (
      <div className={`space-y-4 ${className}`}>
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((id) => (
            <Card key={`skeleton-card-${id}`}>
              <CardContent className="p-6">
                <Skeleton className="h-6 w-3/4 mb-4" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-5/6" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (variant === 'empty') {
    return (
      <div className={`flex flex-col items-center justify-center min-h-[400px] ${className}`}>
        <Database className="h-16 w-16 text-muted-foreground/40 mb-4" />
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <p className="text-sm text-muted-foreground text-center max-w-md mb-4">
          {description}
        </p>
        {onRetry && (
          <Button onClick={onRetry} variant="outline" disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            {isLoading ? 'Loading...' : 'Retry'}
          </Button>
        )}
      </div>
    );
  }

  if (variant === 'error') {
    return (
      <div className={`flex flex-col items-center justify-center min-h-[400px] ${className}`}>
        <div className="rounded-full bg-destructive/10 p-4 mb-4">
          <Database className="h-16 w-16 text-destructive" />
        </div>
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <p className="text-sm text-muted-foreground text-center max-w-md mb-4">
          {description}
        </p>
        {onRetry && (
          <Button onClick={onRetry} variant="outline" disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            {isLoading ? 'Retrying...' : 'Try Again'}
          </Button>
        )}
      </div>
    );
  }

  return null as unknown as React.ReactElement;
}

// Skeleton variants for different layouts
export function TableSkeleton({ rows = 10 }: { readonly rows?: number }): React.ReactElement {
  const rowIds = Array.from({ length: rows }, (_, n) => n + 1);
  return (
    <div className="space-y-2">
      <Skeleton className="h-10 w-full" />
      {rowIds.map((id) => (
        <Skeleton key={`table-row-${id}`} className="h-16 w-full" />
      ))}
    </div>
  );
}

export function GallerySkeleton({ items = 12 }: { readonly items?: number }): React.ReactElement {
  const itemIds = Array.from({ length: items }, (_, n) => n + 1);
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {itemIds.map((id) => (
        <Card key={`gallery-card-${id}`}>
          <CardContent className="p-4">
            <Skeleton className="h-48 w-full mb-4" />
            <Skeleton className="h-6 w-3/4 mb-2" />
            <Skeleton className="h-4 w-full mb-2" />
            <Skeleton className="h-4 w-5/6" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
